import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Planet, Brain, Television, Heart, Sparkle, ArrowRight, Globe, Leaf
} from "@phosphor-icons/react";
import NewsletterForm from "@/components/NewsletterForm";

const PILLARS = [
  { icon: Planet, title: "Earth Innovation", text: "Ecology, healing tech, regenerative systems.", to: "/earth", tone: "earth" },
  { icon: Brain, title: "AI Council", text: "A charter for peaceful human–AI collaboration.", to: "/ai-council", tone: "ink" },
  { icon: Globe, title: "World AI Center", text: "Global inventors, projects, and breakthroughs.", to: "/world-ai", tone: "gold" },
  { icon: Television, title: "WinTV", text: "Stories, interviews, wisdom and field reports.", to: "/wintv", tone: "ink" },
  { icon: Heart, title: "Healing Technologies", text: "Science that serves life and culture.", to: "/blog", tone: "earth" },
  { icon: Sparkle, title: "Inventors & Members", text: "A growing circle of builders and elders.", to: "/members", tone: "gold" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1777047023436-35b85bc0412f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxnbG9iYWwlMjBlYXJ0aCUyMHNwYWNlJTIwYmx1ZXxlbnwwfHx8fDE3ODIwOTQ3NjR8MA&ixlib=rb-4.1.0&q=85)" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink/85" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-32 lg:pt-40 lg:pb-44">
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="overline text-gold" data-testid="hero-eyebrow">
            A Global Innovation Platform
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-white max-w-5xl"
            data-testid="hero-title"
          >
            Inventions, AI, and healing technologies for a world that wants to live.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed"
          >
            WinWorld Innovation gathers inventors, scientists, healers, and elders into
            a single global circle — a place where culture, ecology and intelligence
            collaborate in peace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/world-ai" className="btn-gold rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2" data-testid="hero-cta-primary">
              Explore the Innovation Center <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/wintv" className="rounded-full px-6 py-3 text-sm font-medium border border-white/30 text-white hover:bg-white/10 transition" data-testid="hero-cta-secondary">
              Watch WinTV
            </Link>
          </motion.div>

          <div className="mt-16 max-w-md">
            <p className="overline text-white/70">Newsletter</p>
            <div className="mt-3">
              <NewsletterForm source="hero" theme="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section className="bg-white border-y border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center gap-8">
          <p className="overline shrink-0">Global Circle</p>
          <Marquee gradient gradientColor="#FFFFFF" speed={36} className="text-ink/60 font-display text-xl">
            <span className="mx-10">Healing Labs Lagos</span>
            <span className="mx-10">Andes Wisdom Council</span>
            <span className="mx-10">Seoul Quantum Lab</span>
            <span className="mx-10">Earth Ledger Initiative</span>
            <span className="mx-10">Pacific Sound Research</span>
            <span className="mx-10">Open Science Africa</span>
            <span className="mx-10">Helsinki AI Forum</span>
            <span className="mx-10">Patagonia Restoration</span>
          </Marquee>
        </div>
      </section>

      {/* PILLARS BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <p className="overline">Pillars</p>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl font-light tracking-tight text-ink">
              Six fields, one purpose: <span className="text-gold">life</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg leading-relaxed text-ink/70">
            WinWorld Innovation is not a single product. It is a living mesh of
            initiatives — each rooted in a different field but tuned to the same
            note: a regenerative, peaceful, and inventive civilization.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pillars-grid">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link
                  to={p.to}
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group block bg-white rounded-2xl p-7 ring-1 ring-ink/8 hover:ring-ink/20 card-shadow transition-transform hover:-translate-y-1 h-full"
                >
                  <div className={`inline-flex w-11 h-11 items-center justify-center rounded-xl ${p.tone === "gold" ? "bg-gold/15 text-gold" : p.tone === "earth" ? "bg-earth/15 text-earth" : "bg-ink/8 text-ink"}`}>
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl text-ink tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-base text-ink/65 leading-relaxed">{p.text}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm text-ink/80 group-hover:text-gold transition-colors">
                    Discover <ArrowRight size={14} weight="bold" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* MISSION SPLIT */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1684775622511-c76e31cd404b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxuYXR1cmUlMjBlY29sb2d5JTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODIwOTQ3NjR8MA&ixlib=rb-4.1.0&q=85"
                alt="A hand cradling earth"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="overline">Our Stance</p>
            <h2 className="mt-4 font-display text-3xl lg:text-5xl font-light tracking-tight text-ink leading-[1.1]">
              We treat the planet as the first invention — and intelligence as a sacred guest.
            </h2>
            <div className="mt-8 space-y-5 text-lg text-ink/70 leading-relaxed">
              <p>
                Every initiative inside WinWorld answers to a single test: does it
                increase the conditions for life? Healing, ecology, culture, and
                AI are not silos — they are instruments in the same ensemble.
              </p>
              <p>
                We invite inventors and elders, scientists and storytellers, to
                build with us. The result is a quieter kind of progress — one
                that the next century will thank us for.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/ai-council" className="btn-ink rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2" data-testid="mission-cta-council">
                Read the AI Council Charter <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/earth" className="btn-outline-ink rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
                Visit Earth Innovation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-4 gap-10">
          {[
            { k: "37+", v: "Countries represented in our growing circle" },
            { k: "6", v: "Pillars working as one regenerative mesh" },
            { k: "120+", v: "Inventors & elders in active conversation" },
            { k: "∞", v: "Bandwidth for ideas that serve life" },
          ].map((s) => (
            <div key={s.v} data-testid={`stat-${s.k}`}>
              <p className="font-display text-5xl lg:text-6xl text-gold font-light">{s.k}</p>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="rounded-3xl bg-white ring-1 ring-ink/10 card-shadow p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="overline">Join the dispatch</p>
            <h3 className="mt-4 font-display text-3xl lg:text-4xl text-ink font-light tracking-tight">
              One thoughtful letter every few weeks. Inventions, interviews, and ideas worth your time.
            </h3>
          </div>
          <div className="lg:col-span-5">
            <NewsletterForm source="home-cta" />
            <p className="mt-3 text-xs text-ink/50">No spam. Unsubscribe in one click.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
